<template>
  <v-card>
    <v-toolbar
      class="settings-window-toolbar"
      extension-height="40"
      flat
      height="80"
    >
      <v-avatar
        :color="$t('Settings.Payment.Toolbar.Color')"
      >
        <v-icon dark>
          {{ $t('Settings.Payment.Toolbar.Icon') }}
        </v-icon>
      </v-avatar>
      <v-toolbar-title class="pl-3">
        <div class="body-3">
          {{ $t('Settings.Payment.Toolbar.Title') }}
        </div>

        <div class="text-caption">
          {{ $t('Settings.Payment.Toolbar.Subtitle') }}
        </div>
      </v-toolbar-title>

      <v-spacer />

      <template #extension>
        <v-tabs
          v-model="tab"
          background-color="transparent"
          grow
          height="40"
          slider-color="blue"
        >
          <v-tab key="gateway">
            {{ $t('Settings.Payment.Toolbar.Tab.Gateway').toLocaleUpperCase($i18n.locale) }}
          </v-tab>

          <v-tab
            v-if="appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD"
            key="terminal"
          >
            {{ $t('Settings.Payment.Toolbar.Tab.Terminal').toLocaleUpperCase($i18n.locale) }}
          </v-tab>
        </v-tabs>
      </template>
    </v-toolbar>

    <v-divider />

    <v-card-text
      id="settingsWindow"
      style="overflow-y: auto;"
    >
      <template v-if="viewData">
        <v-tabs-items v-model="tab">
          <v-tab-item key="gateway">
            <v-container
              class="pa-0"
              fluid
            >
              <v-row
                align="center"
                dense
              >
                <v-col cols="12">
                  <h5 class="text-body-2 font-weight-bold">
                    <lang-search-highlight
                      :lang-text="$t('Settings.Payment.ListView.Title')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h5>
                  <span class="text-caption">
                    <lang-search-highlight
                      :lang-text="$t('Settings.Payment.ListView.Subtitle')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </span>
                </v-col>

                <v-col cols="12">
                  <v-list three-line>
                    <template v-for="(paymentMethod, i) in viewData.Payments">
                      <template v-if="userIsSuperExtraAdmin || paymentMethod.Status">
                        <v-list-item
                          :key="'list_tile_' + i"
                          :disabled="!userCanManage || paymentMethod.Disabled"
                        >
                          <div
                            v-if="userIsSuperAdmin"
                            :class="[paymentMethod.Status ? 'light-green' : 'grey lighten-1']"
                            class="ma-0 pa-0"
                            style="width: 5px; height: 100%; position: absolute; left: 0"
                          />

                          <v-list-item-avatar
                            :color="!!paymentMethod.Image ? 'transparent' : paymentMethod.Color1"
                            class="mr-3"
                          >
                            <img
                              v-if="!!paymentMethod.Image"
                              :src="`${appConfig.LOCATION_DATA.CdnUrl}assets/payments/${paymentMethod.Image}`"
                              height="22"
                              width="auto"
                            >
                            <v-icon
                              v-else
                              dark
                            >
                              {{ paymentMethod.Icon }}
                            </v-icon>
                          </v-list-item-avatar>

                          <v-list-item-content>
                            <v-list-item-title>
                              {{ paymentMethod.ModuleTitle }}
                            </v-list-item-title>

                            <v-list-item-subtitle>
                              {{ getTranslatedField(paymentMethod, 'Name', 'Lang', true, 'Title') }}
                              <span
                                v-if="getPaymentMethodName(paymentMethod)"
                                class="grey--text"
                              >
                                ({{ getPaymentMethodName(paymentMethod) }})
                              </span>
                            </v-list-item-subtitle>

                            <v-list-item-subtitle>
                              <template v-if="paymentMethod.Pos">
                                <span class="pr-1 text-caption">
                                  <v-icon
                                    color="orange"
                                    size="16"
                                  >
                                    add_shopping_cart
                                  </v-icon>
                                  {{ $t('Settings.Payment.ListView.UsedPos') }}
                                </span>
                              </template>

                              <template v-if="paymentMethod.Table">
                                <span class="pr-1 text-caption">
                                  <v-icon
                                    color="teal"
                                    size="16"
                                  >
                                    tab_unselected
                                  </v-icon>
                                  {{ $t('Settings.Payment.ListView.UsedTable') }}
                                </span>
                              </template>

                              <template v-if="paymentMethod.Site">
                                <span class="pr-1 text-caption">
                                  <v-icon
                                    color="blue"
                                    size="16"
                                  >
                                    language
                                  </v-icon>
                                  {{ $t('Settings.Payment.ListView.UsedWebsite') }}
                                </span>
                              </template>
                            </v-list-item-subtitle>
                          </v-list-item-content>

                          <v-list-item-action v-if="userCanManage && !paymentMethod.Disabled">
                            <v-btn
                              color="grey darken-2"
                              icon
                              text
                              @click.prevent="onEditPaymentMethodClick(paymentMethod)"
                            >
                              <v-icon>edit</v-icon>
                            </v-btn>
                          </v-list-item-action>
                        </v-list-item>

                        <v-divider :key="'list_tile_divider' + i" />
                      </template>
                    </template>
                  </v-list>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>

          <v-tab-item
            v-if="appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD"
            key="terminal"
          >
            <v-expansion-panels
              v-model="expansionPanel"
              flat
              focusable
            >
              <!-- Vendors -->
              <template v-for="vendor in viewData.Vendors">
                <v-expansion-panel :key="`vendor-${vendor.Id}`">
                  <v-expansion-panel-header class="pl-2">
                    <div class="d-flex justify-start align-center">
                      <div
                        v-if="Object.values(TerminalVendorEnum).includes(vendor.Code)"
                        class="justify-start align-center mr-6"
                      >
                        <v-avatar
                          class="rounded pa-2"
                          color="grey lighten-3"
                          height="60"
                          tile
                          width="120"
                        >
                          <v-img
                            :src="`/assets/img/payment/logo-${vendor.Code}-dark.png`"
                            contain
                            height="auto"
                            width="120"
                          />
                        </v-avatar>
                      </div>

                      <div class="justify-start align-start">
                        <div class="d-flex">
                          <h4 class="text-h6 font-weight-bold">
                            <span
                              class="d-inline-flex"
                              style="min-width: 140px;"
                            >
                              <lang-search-highlight
                                :lang-text="getVendorById(vendor.Id).Name"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>

                            <span class="text-body-2 grey--text ml-2">
                              <lang-search-highlight
                                :lang-text="getVendorById(vendor.Id).Protocol"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </h4>
                        </div>

                        <div class="d-flex mt-1">
                          <h5 class="text-body-2 font-weight-normal blue-grey--text">
                            <lang-search-highlight
                              :lang-text="getVendorAcquirersList(vendor.Id)"
                              :search-text="langSearchText"
                              highlight
                            />
                          </h5>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-divider />

                  <v-expansion-panel-content class="no-padding">
                    <v-toolbar flat>
                      <template v-if="userIsSuperAdmin">
                        <v-menu offset-y>
                          <template #activator="{on}">
                            <v-btn
                              class="mr-2 px-1"
                              color="blue-grey"
                              dark
                              depressed
                              v-on="on"
                            >
                              COPY FAQ
                            </v-btn>
                          </template>

                          <v-sheet class="py-1 px-2">
                            <v-btn
                              block
                              class="my-1 px-2"
                              color="blue-grey"
                              dark
                              depressed
                              @click="onCopySupportedBanks(vendor, 'el')"
                            >
                              EL - BANKS LIST
                            </v-btn>
                            <v-btn
                              block
                              class="my-1 px-2"
                              color="blue-grey"
                              dark
                              depressed
                              @click="onCopySupportedBanks(vendor, 'en')"
                            >
                              EN - BANKS LIST
                            </v-btn>

                            <v-btn
                              block
                              class="my-1 px-2"
                              color="blue-grey"
                              dark
                              depressed
                              @click="onCopySupportedTerminals(vendor, 'el')"
                            >
                              EL - TERMINALS LIST
                            </v-btn>
                            <v-btn
                              block
                              class="my-1 px-2"
                              color="blue-grey"
                              dark
                              depressed
                              @click="onCopySupportedTerminals(vendor, 'en')"
                            >
                              EN - TERMINALS LIST
                            </v-btn>
                          </v-sheet>
                        </v-menu>

                        <v-btn-toggle
                          v-if="userIsSuperAdmin && vendor.Code !== TerminalVendorEnum.VIVA"
                          v-model="vendor.Staging"
                          :color="vendor.Staging ? 'orange' : 'green'"
                          class="elevation-0"
                          dense
                          mandatory
                          @change="updateVendorEnv(vendor)"
                        >
                          <v-btn
                            :value="false"
                            text
                          >
                            LIVE
                          </v-btn>

                          <v-btn
                            :value="true"
                            text
                          >
                            STAGING
                          </v-btn>
                        </v-btn-toggle>
                      </template>

                      <v-spacer />

                      <v-btn
                        v-if="vendor.Code === TerminalVendorEnum.VIVA"
                        class="mr-2"
                        color="blue-grey"
                        dark
                        depressed
                        @click="onEditVendor(vendor)"
                      >
                        <v-icon left>
                          mdi-pencil
                        </v-icon>
                        {{ $t('Common.Button.Edit') }}
                      </v-btn>

                      <v-btn
                        class="mr-2"
                        color="primary"
                        depressed
                        @click="onAddVendorTerminal(vendor)"
                      >
                        <v-icon left>
                          mdi-plus
                        </v-icon>
                        {{ $t('Common.Button.Add') }} EFT/POS
                      </v-btn>
                    </v-toolbar>

                    <v-divider />

                    <v-container
                      class="pa-0 py-2"
                      fluid
                    >
                      <v-row
                        align="center"
                        dense
                      >
                        <v-col cols="12">
                          <div class="divTable">
                            <div class="divTableHeading">
                              <div class="divTableRow">
                                <div class="divTableHead text-left">
                                  Active
                                </div>

                                <div class="divTableHead text-left">
                                  Name
                                </div>

                                <div class="divTableHead text-left">
                                  Acquirer
                                </div>

                                <div class="divTableHead text-left">
                                  Manufacturer
                                </div>

                                <div class="divTableHead text-left">
                                  Model
                                </div>

                                <div class="divTableHead text-left">
                                  Operating System
                                </div>

                                <div class="divTableHead text-left">
                                  Connection
                                </div>

                                <div class="divTableHead text-left">
                                  <template v-if="vendor.Code === TerminalVendorEnum.VIVA">
                                    Virtual Terminal Id
                                  </template>

                                  <template v-else-if="vendor.Code === TerminalVendorEnum.MELLON || vendor.Code === TerminalVendorEnum.EPAY">
                                    TId
                                  </template>

                                  <template v-else>
                                    Id
                                  </template>
                                </div>

                                <div class="divTableHead text-left">
                                  Terminal Id
                                </div>

                                <div class="divTableHead text-center">
                                  Guide
                                </div>

                                <div
                                  class="divTableHead text-right"
                                  style="max-width: 20px; width: 20px;"
                                />
                              </div>
                            </div>

                            <div
                              v-for="terminal in vendor.Terminals"
                              :key="`vendor-${vendor.Id}terminal-${terminal.Id}`"
                              class="divTableRow"
                            >
                              <div class="divTableCell text-left">
                                <v-icon
                                  v-if="terminal.Active"
                                  color="green"
                                >
                                  mdi-check-circle-outline
                                </v-icon>
                                <v-icon
                                  v-else
                                  color="grey"
                                >
                                  mdi-close-circle-outline
                                </v-icon>
                              </div>

                              <div class="divTableCell text-left">
                                {{ terminal.Nickname }}
                              </div>

                              <div class="divTableCell text-left">
                                {{ getVendorAcquirerTextById(vendor.Id, terminal.AcquirerCode) }}
                              </div>

                              <div class="divTableCell text-left">
                                {{ terminal.Manufactor }}
                              </div>

                              <div class="divTableCell text-left">
                                {{ terminal.Model }}
                              </div>

                              <div class="divTableCell text-left">
                                {{ terminal.OperationSystem }}
                              </div>

                              <div class="divTableCell text-left">
                                {{ terminal.Connection }}
                              </div>

                              <div class="divTableCell text-left">
                                {{ terminal.VirtualTerminalId || terminal.TId }}
                              </div>

                              <div class="divTableCell text-left">
                                {{ terminal.TerminalId }}
                              </div>

                              <div class="divTableCell text-center">
                                <div v-if="terminal.PosVendorId && terminal.ModelId">
                                  <v-tooltip
                                    color="info"
                                    left
                                  >
                                    <template #activator="{on}">
                                      <v-btn
                                        :disabled="!getTerminalErpGuideUrl(terminal.PosVendorId, terminal.ModelId)"
                                        :href="getTerminalErpGuideUrl(terminal.PosVendorId, terminal.ModelId)"
                                        color="info"
                                        icon
                                        small
                                        target="_blank"
                                        v-on="on"
                                      >
                                        <v-icon>
                                          mdi-information-variant-circle-outline
                                        </v-icon>
                                      </v-btn>
                                    </template>

                                    {{ $t('Settings.Payment.AddTerminal.TerminalGuide') }}
                                  </v-tooltip>
                                </div>
                              </div>

                              <div class="divTableCell text-right">
                                <v-speed-dial
                                  v-model="terminal.fab"
                                  direction="left"
                                  transition="slide-x-reverse-transition"
                                >
                                  <template #activator>
                                    <v-btn
                                      icon
                                      small
                                    >
                                      <v-icon>mdi mdi-dots-vertical</v-icon>
                                    </v-btn>
                                  </template>

                                  <v-tooltip
                                    color="grey"
                                    top
                                  >
                                    <template #activator="{on}">
                                      <v-btn
                                        class="grey ma-0 ml-2"
                                        dark
                                        icon
                                        small
                                        @click="onEditVendorTerminal(terminal)"
                                        v-on="on"
                                      >
                                        <v-icon small>
                                          mdi-pencil
                                        </v-icon>
                                      </v-btn>
                                    </template>

                                    <span>{{ $t('Common.Button.Edit') }}</span>
                                  </v-tooltip>

                                  <v-tooltip
                                    color="red"
                                    top
                                  >
                                    <template #activator="{on}">
                                      <v-btn
                                        class="red ma-0 ml-2"
                                        dark
                                        icon
                                        small
                                        @click="onDeleteVendorTerminal(terminal)"
                                        v-on="on"
                                      >
                                        <v-icon small>
                                          mdi-close
                                        </v-icon>
                                      </v-btn>
                                    </template>

                                    <span>{{ $t('Common.Button.Delete') }}</span>
                                  </v-tooltip>
                                </v-speed-dial>
                              </div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </template>
            </v-expansion-panels>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4 settings-window-actions">
      <v-spacer />
      <v-btn
        class="light-blue--text text--darken-1"
        outlined
        to="/system-settings"
      >
        {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
    </v-card-actions>

    <terminal-vendor-edit-dialog
      v-if="userCanManage"
      :terminal-vendor="selectedTerminalVendor"
      :visible.sync="terminalVendorEditDialogVisible"
      @terminal-vendor-save="onTerminalVendorSave"
    />

    <terminal-edit-dialog
      v-if="userCanManage"
      :payment-terminal="selectedPaymentTerminal"
      :visible.sync="terminalEditDialogVisible"
      @payment-terminal-save="onPaymentTerminalSave"
    />

    <viva-terminal-add-dialog
      v-if="userCanManage"
      :vendor="selectedTerminalVendor"
      :visible.sync="vivaTerminalAddDialogVisible"
      @terminal-added="getData"
    />

    <mellon-terminal-add-dialog
      v-if="userCanManage"
      :vendor="selectedTerminalVendor"
      :visible.sync="mellonTerminalAddDialogVisible"
      @terminal-added="getData"
    />

    <nexi-terminal-add-dialog
      v-if="userCanManage"
      :vendor="selectedTerminalVendor"
      :visible.sync="nexiTerminalAddDialogVisible"
      @terminal-added="getData"
    />

    <e-pay-terminal-add-dialog
      v-if="userCanManage"
      :vendor="selectedTerminalVendor"
      :visible.sync="epayTerminalAddDialogVisible"
      @terminal-added="getData"
    />

    <edps-terminal-add-dialog
      v-if="userCanManage"
      :vendor="selectedTerminalVendor"
      :visible.sync="edpsTerminalAddDialogVisible"
      @terminal-added="getData"
    />

    <payment-edit-dialog
      v-if="userCanManage"
      :payment-method="selectedPaymentMethod"
      :visible.sync="editDialogVisible"
      @payment-method-save="onPaymentMethodChanged"
    />

    <confirm-dialog
      v-if="confirmDialogDeleteTerminalVisible && userCanDelete"
      :html-content="$tc('Settings.Payment.Dialog.DeleteTerminal.Content', 1, {terminal: selectedDeleteTerminalName})"
      :html-title="$t('Settings.Payment.Dialog.DeleteTerminal.Title')"
      :info-bar="true"
      :info-bar-body="$t('Charge.Dialog.Delete.Notification')"
      :visible.sync="confirmDialogDeleteTerminalVisible"
      :width="560"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmDeleteTerminal"
    />
  </v-card>
</template>

<script>
import PaymentEditDialog from './payments/PaymentEditDialog'
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import Translatable from '@/mixins/translatable'
import PaymentTerminalModel from '@/api/Models/settings/PaymentTerminalModel'
import TerminalEditDialog from '@/components/settings/payments/TerminalEditDialog.vue'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import TerminalVendorEditDialog from '@/components/settings/payments/TerminalVendorEditDialog.vue'
import MellonTerminalAddDialog from '@/components/settings/payments/MellonTerminalAddDialog.vue'
import TerminalVendorEnum from '@/api/Enums/TerminalVendorEnum'
import VivaTerminalAddDialog from '@/components/settings/payments/VivaTerminalAddDialog.vue'
import EPayTerminalAddDialog from '@/components/settings/payments/EPayTerminalAddDialog.vue'
import TerminalsCommon from '@/mixins/payments/terminalsCommon'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import LangSearchHighlight from '@/lang/highlight/LangSearchHighlight.vue'
import LangSearchHighlightMixin from '@/lang/highlight/langSearchHighlightMixin.js'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import EdpsTerminalAddDialog from '@/components/settings/payments/EdpsTerminalAddDialog.vue'
import PaymentType from '@/mixins/orders/paymentType.js'
import NexiTerminalAddDialog from '@/components/settings/payments/NexiTerminalAddDialog.vue'

export default {
  components: {
    NexiTerminalAddDialog,
    EdpsTerminalAddDialog,
    LangSearchHighlight,
    ConfirmDialog,
    EPayTerminalAddDialog,
    VivaTerminalAddDialog,
    MellonTerminalAddDialog,
    TerminalVendorEditDialog,
    TerminalEditDialog,
    PaymentEditDialog
  },
  mixins: [AppData, Auth, Translatable, TerminalsCommon, LangSearchHighlightMixin, PaymentType],
  data() {
    return {
      tab                               : null,
      expansionPanel                    : null,
      editDialogVisible                 : false,
      terminalEditDialogVisible         : false,
      mellonTerminalAddDialogVisible    : false,
      nexiTerminalAddDialogVisible      : false,
      epayTerminalAddDialogVisible      : false,
      edpsTerminalAddDialogVisible      : false,
      vivaTerminalAddDialogVisible      : false,
      terminalVendorEditDialogVisible   : false,
      confirmDialogDeleteTerminalVisible: false,
      selectedPaymentMethod             : null,
      selectedPaymentTerminal           : null,
      selectedDeleteTerminal            : null,
      selectedTerminalVendor            : null,
      viewData                          : null
    }
  },
  computed: {
    TerminalVendorEnum() {
      return TerminalVendorEnum
    },
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },

    selectedDeleteTerminalName() {
      const terminal = this.selectedDeleteTerminal
      if (!terminal) return ''

      const acquirer = this.getVendorAcquirerTextById(terminal.PosVendorId, terminal.AcquirerCode)

      return terminal.Nickname + (acquirer ? ` - ${acquirer}` : '')
    }
  },
  watch: {},
  created() {
    // console.log('1. created');
  },
  mounted() {
    // console.log('2. mounted');
    this.$bus.$on(window.SocketCommand.Settings.Payments.Get, this.onGetPaymentsResult)
    this.$bus.$on(window.SocketCommand.Settings.Payments.Save, this.onSavePaymentResult)
    this.$bus.$on(window.SocketCommand.Settings.Payments.Vendor.Env, this.onUpdateVendorEnvResult)
    this.$bus.$on(window.SocketCommand.Settings.Payments.Vendor.Save, this.onSaveTerminalVendorResult)
    this.$bus.$on(window.SocketCommand.Settings.Payments.Terminal.Save, this.onSavePaymentTerminalResult)
    this.$bus.$on(window.SocketCommand.Settings.Payments.Terminal.Delete, this.onDeleteVendorTerminalResult)
    this.getData()
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Settings.Payments.Get, this.onGetPaymentsResult)
    this.$bus.$off(window.SocketCommand.Settings.Payments.Save, this.onSavePaymentResult)
    this.$bus.$off(window.SocketCommand.Settings.Payments.Vendor.Env, this.onUpdateVendorEnvResult)
    this.$bus.$off(window.SocketCommand.Settings.Payments.Vendor.Save, this.onSaveTerminalVendorResult)
    this.$bus.$off(window.SocketCommand.Settings.Payments.Terminal.Save, this.onSavePaymentTerminalResult)
    this.$bus.$off(window.SocketCommand.Settings.Payments.Terminal.Delete, this.onDeleteVendorTerminalResult)
    // console.log('4. destroyed');
  },
  methods: {
    onCopySupportedTerminals(vendor, locale = 'el') {
      if (!vendor) return
      const vendors = Object.values(this.$t('Settings.Payment.Terminal.Vendor', locale))
      const acquirers = vendors?.find(v => v.Id === vendor.Id)?.Acquirer

      let retVal = ''
      retVal += '<div>'
      retVal += `<p class="font-weight-bold">${locale ? 'Υποστηριζόμενα Τερματικά EFT/POS' : 'Supported EFT/POS Terminals'}</p>`
      retVal += '<p></p>'
      acquirers.forEach(acquirer => {
        const terminals = this.getVendorAcquirerTerminals(vendor.Id, acquirer.Value)
        const acquirerName = acquirer.SubAcquirer === acquirer.Name ? acquirer.SubAcquirer : `${acquirer.SubAcquirer} - ${acquirer.Name}`

        retVal += '<div>'
        retVal += `  <div class="font-weight-bold">${acquirerName}</div>`
        retVal += '  <ul>'
        terminals.forEach(terminal => {
          retVal += `    <li>${terminal.Manufacturer} ${terminal.Model}</li>`
        })
        retVal += '  </ul>'
        retVal += '</div>'
        retVal += '<p></p>'
      })
      retVal += '</div>'

      this.copyToClipboard(retVal)
    },
    onCopySupportedBanks(vendor, locale = 'el') {
      if (!vendor) return
      this.copyToClipboard(this.getVendorAcquirersList(vendor.Id, locale))
    },


    getPaymentMethodName(paymentMethod) {
      const retVal = this.getPaymentTypeLangByCode(paymentMethod?.Code)?.Title || paymentMethod?.Name || ''
      return retVal.toLocaleUpperCase(this.$i18n.locale)
    },

    onEditVendor(vendor) {
      this.selectedTerminalVendor = vendor
      this.terminalVendorEditDialogVisible = true
    },

    onAddVendorTerminal(vendor) {
      this.selectedTerminalVendor = vendor

      if (vendor.Code === TerminalVendorEnum.VIVA) {
        this.vivaTerminalAddDialogVisible = true
      } else if (vendor.Code === TerminalVendorEnum.MELLON) {
        this.mellonTerminalAddDialogVisible = true
      } else if (vendor.Code === TerminalVendorEnum.NEXI) {
        this.nexiTerminalAddDialogVisible = true
      } else if (vendor.Code === TerminalVendorEnum.EPAY) {
        this.epayTerminalAddDialogVisible = true
      } else if (vendor.Code === TerminalVendorEnum.EDPS) {
        this.edpsTerminalAddDialogVisible = true
      }
    },
    onEditVendorTerminal(terminal) {
      this.selectedPaymentTerminal = new PaymentTerminalModel(terminal)
      this.terminalEditDialogVisible = true
    },

    onDeleteVendorTerminal(terminal) {
      this.selectedDeleteTerminal = terminal
      this.confirmDialogDeleteTerminalVisible = true
    },
    onConfirmDeleteTerminal() {
      if (!this.selectedDeleteTerminal) return
      window.callAS(window.SocketCommand.Settings.Payments.Terminal.Delete, {Id: this.selectedDeleteTerminal.Id})
    },
    onDeleteVendorTerminalResult(result) {
      if (result.status === 'success') {
        this.getData()
        const terminalName = this.selectedDeleteTerminalName
        this.selectedDeleteTerminal = null
        this.$bus.$emit('app-show-notification', {
          body: this.$tc('Settings.Payment.Dialog.DeleteTerminal.Success', 1, {terminal: terminalName}),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Charge.Notification.Delete'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    getData() {
      window.callAS(window.SocketCommand.Settings.Payments.Get, {})
    },
    onGetPaymentsResult(result) {
      if (!result) return

      const payments = result?.Payments || []

      payments.forEach(payment => {
        payment.OrderStatus = parseInt(payment.OrderStatus)
        if (payment.OrderStatus === 0) payment.OrderStatus = 11
      })

      result.Payments = payments

      this.viewData = result
    },

    onEditPaymentMethodClick(paymentMethod) {
      this.selectedPaymentMethod = JSON.parse(JSON.stringify(paymentMethod))
      this.editDialogVisible = true
    },

    onTerminalVendorSave(data) {
      window.callAS(window.SocketCommand.Settings.Payments.Vendor.Save, data || {})
    },
    onSaveTerminalVendorResult(result) {
      if (result.status === 'success') {
        this.terminalVendorEditDialogVisible = false
        this.getData()
      }
    },

    onPaymentTerminalSave(data) {
      window.callAS(window.SocketCommand.Settings.Payments.Terminal.Save, data || {})
    },
    onSavePaymentTerminalResult(result) {
      if (result.status === 'success') {
        this.terminalEditDialogVisible = false
        this.getData()
      }
    },

    updateVendorEnv(vendor) {
      if (!vendor?.Id) return

      window.callAS(window.SocketCommand.Settings.Payments.Vendor.Env, {Id: vendor.Id, Staging: vendor.Staging})
    },
    onUpdateVendorEnvResult(result) {
      this.getData()
    },

    onPaymentMethodChanged(data) {
      window.callAS(window.SocketCommand.Settings.Payments.Save, {Payment: data})
    },
    onSavePaymentResult(result) {
      if (result.status === 'success') {
        this.editDialogVisible = false
        this.getData()
        this.$bus.$emit('app-show-notification', {
          body: result.type,
          type: 'success',
          icon: 'check'
        })
      } else {
        if (result.hasOwnProperty('gateway') && result.gateway === PaymentTypeEnum.VIVA_3DS) {
          this.$bus.$emit('app-show-notification', {
            body   : this.$t('Settings.Payment.Notification.VivaWallet'),
            type   : 'error',
            icon   : 'warning',
            timeout: 3000
          })
          return
        }

        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Settings.Payment.Notification.Update'),
          type   : 'error',
          icon   : 'warning',
          timeout: 3000
        })
      }
    }
  }
}
</script>

<style scoped>

.divTableCell, .divTableHead {
  padding: 3px 4px;
}

:deep(.no-padding .v-expansion-panel-content__wrap) {
  padding: 0
}
</style>
